if (!customElements.get("scroll-gallery")) {
  customElements.define(
    "scroll-gallery",
    class ScrollGallery extends HTMLElement {
      connectedCallback() {
        this.carousel = this.querySelector("flickity-component");
        this.autoMoveOnScroll = this.dataset.autoMoveOnScroll !== "false";
        this.scrollDistance = Math.max(
          40,
          Number(this.dataset.scrollDistance) || 200
        );
        this.minimumMovementInterval = 500;
        this.lastMovementTime = 0;
        this.lastScrollY = window.scrollY;
        this.accumulatedScroll = 0;
        this.scrollDirection = 0;
        this.isVisible = false;
        this.frame = null;
        this.prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        );

        this.handleScroll = this.onScroll.bind(this);
        this.handleWindowLoad = this.initLightbox.bind(this);

        if (this.autoMoveOnScroll) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              this.isVisible = entry.isIntersecting;
              this.lastScrollY = window.scrollY;
              this.accumulatedScroll = 0;
            },
            { threshold: 0.05 }
          );
          this.observer.observe(this);
          window.addEventListener("scroll", this.handleScroll, {
            passive: true,
          });
        }

        if (window.FoxTheme?.PhotoSwipeLightbox) {
          this.initLightbox();
        } else {
          window.addEventListener("load", this.handleWindowLoad, {
            once: true,
          });
        }
      }

      disconnectedCallback() {
        window.removeEventListener("scroll", this.handleScroll);
        window.removeEventListener("load", this.handleWindowLoad);
        this.observer?.disconnect();

        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.lightbox) this.lightbox.destroy();
      }

      initLightbox() {
        if (this.lightbox || !window.FoxTheme?.PhotoSwipeLightbox) return;

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          gallery: this,
          children: ".scroll-gallery__image-link",
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.ui.registerElement({
            name: "prev",
            ariaLabel: "Previous slide",
            order: 1,
            isButton: true,
            html: '<svg class="pswp-icon-prev" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow"></path></svg>',
            onClick: () => {
              this.lightbox.pswp.prev();
            },
          });

          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="f-icon-svg f-icon--medium f-icon-close"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: () => {
              this.lightbox.pswp.close();
            },
          });

          this.lightbox.pswp.ui.registerElement({
            name: "next",
            ariaLabel: "Next slide",
            order: 3,
            isButton: true,
            html: '<svg class="pswp-icon-next" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180)"></path></svg>',
            onClick: () => {
              this.lightbox.pswp.next();
            },
          });
        });

        this.lightbox.init();
      }

      onScroll() {
        if (!this.autoMoveOnScroll) return;

        const currentScrollY = window.scrollY;
        const delta = currentScrollY - this.lastScrollY;
        this.lastScrollY = currentScrollY;

        if (
          !this.isVisible ||
          this.prefersReducedMotion.matches ||
          Math.abs(delta) < 1
        ) {
          return;
        }

        const direction = delta > 0 ? 1 : -1;
        if (direction !== this.scrollDirection) {
          this.scrollDirection = direction;
          this.accumulatedScroll = 0;
        }

        this.accumulatedScroll += Math.abs(delta);
        const currentTime = performance.now();
        if (
          this.accumulatedScroll < this.scrollDistance ||
          currentTime - this.lastMovementTime < this.minimumMovementInterval ||
          this.frame
        ) {
          return;
        }

        this.accumulatedScroll %= this.scrollDistance;
        this.lastMovementTime = currentTime;
        this.frame = requestAnimationFrame(() => {
          const slider = this.carousel?.slider?.instance;

          if (slider && !slider.isPointerDown) {
            if (this.scrollDirection > 0) {
              slider.next(true);
            } else {
              slider.previous(true);
            }
          }

          this.frame = null;
        });
      }
    }
  );
}
